import api from './api';
import { refreshSession, SESSION_EXPIRED_FLAG } from './session';
import { AuthResponse, User } from '@/types';

// Helper function to decode JWT token
function parseJwt(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error parsing JWT:', e);
    return null;
  }
}

export const authService = {
  async login(email: string, password: string): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/api/v1/auth/employee/signin', {
        email,
        password,
      });

      const data = response.data;

      if (data.accessToken) {
        sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('idToken', data.idToken);
        localStorage.setItem('refreshToken', data.refreshToken);

        // If userInfo is missing, try to get it from the ID token
        if (!data.userInfo && data.idToken) {
          const decodedToken = parseJwt(data.idToken);

          // Construct userInfo from token claims
          const userInfo: User = {
            email: decodedToken.email || decodedToken['cognito:username'],
            firstName: decodedToken.given_name || decodedToken.name?.split(' ')[0] || '',
            lastName: decodedToken.family_name || decodedToken.name?.split(' ')[1] || '',
            roles: ['admin'], // Admin portal users should have admin role
            userType: 'employee',
          };

          localStorage.setItem('userInfo', JSON.stringify(userInfo));
          data.userInfo = userInfo;
        } else if (data.userInfo) {
          localStorage.setItem('userInfo', JSON.stringify(data.userInfo));
        }
      }

      return data;
    } catch (error) {
      console.error('Login service error:', error);
      throw error;
    }
  },

  logout(): void {
    if (typeof window !== 'undefined') {
      // Clear all auth-related data
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userInfo');
      
      // Optional: Clear all localStorage (uncomment if needed)
      // localStorage.clear();
      
    }
  },

  getCurrentUser(): User | null {
    if (typeof window === 'undefined') return null;

    const userInfo = localStorage.getItem('userInfo');
    return userInfo ? JSON.parse(userInfo) : null;
  },

  getAccessToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('accessToken');
  },

  isAuthenticated(): boolean {
    if (typeof window === 'undefined') return false;

    return !!localStorage.getItem('accessToken');
  },

  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user?.roles?.includes(role) || false;
  },

  /** Renews the access token with the refresh token. */
  async refreshToken(): Promise<boolean> {
    return (await refreshSession()) !== null;
  },
};
